import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, DollarSign, Loader2, TrendingUp, Clock, AlertTriangle, CheckCircle, ArrowRight, Plus, UserPlus, Utensils, Package, CalendarCheck, CreditCard, FileText, FileQuestion } from 'lucide-react';
import { eventsAPI, contractsAPI, invoicesAPI, inventoryAPI, quoteRequestsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { getBusinessLine, BUSINESS_LINES, businessLineColor } from '../utils/businessLine';
import { parseLocalDate, formatLocalDate } from '../utils/dateUtils';

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [events, setEvents] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [eventsData, contractsData] = await Promise.all([
        eventsAPI.getAll(),
        contractsAPI.getAll()
      ]);
      setEvents(eventsData);
      setContracts(contractsData);
      
      // Try to fetch invoice stats and inventory alerts
      try {
        const stats = await invoicesAPI.getStats();
        setInvoiceStats(stats);
      } catch (e) {
        console.warn('Invoice stats unavailable:', e);
      }
      
      try {
        const alerts = await inventoryAPI.getAlerts();
        setLowStockAlerts(alerts);
      } catch (e) {
        console.warn('Inventory alerts unavailable:', e);
      }
      
      try {
        const quotes = await quoteRequestsAPI.getAll();
        setQuoteRequests(quotes);
      } catch (e) {
        console.warn('Quote requests unavailable:', e);
      }
    } catch (error) {
      toast({ title: 'Error loading dashboard', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const thisWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  const thisMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  // Today's events
  const todaysEvents = contracts.filter(c => c.event_date === todayStr);

  const upcomingThisWeek = contracts.filter(c => {
    const eventDate = parseLocalDate(c.event_date);
    return eventDate && eventDate >= today && eventDate <= thisWeek;
  });

  const upcomingThisMonth = contracts.filter(c => {
    const eventDate = parseLocalDate(c.event_date);
    return eventDate && eventDate >= today && eventDate <= thisMonth;
  });

  const pendingContracts = contracts.filter(c => c.status === 'pending' || c.status === 'draft');
  const confirmedContracts = contracts.filter(c => c.status === 'confirmed');
  
  const totalRevenue = contracts.reduce((sum, c) => sum + (c.total_amount || 0), 0);
  const monthlyRevenue = contracts
    .filter(c => {
      const date = parseLocalDate(c.event_date);
      return date && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
    })
    .reduce((sum, c) => sum + (c.total_amount || 0), 0);

  const totalGuests = contracts.reduce((sum, c) => sum + (c.guest_count || 0), 0);

  // Business-line segmentation (derived from event_type)
  const businessLineStats = BUSINESS_LINES.map(line => {
    const matching = contracts.filter(c => getBusinessLine(c.event_type) === line);
    const upcoming = matching.filter(c => {
      const d = parseLocalDate(c.event_date);
      return d && d >= today;
    });
    return {
      line,
      count: matching.length,
      upcoming: upcoming.length,
      revenue: matching.reduce((s, c) => s + (c.total_amount || 0), 0),
      guests: matching.reduce((s, c) => s + (c.guest_count || 0), 0),
    };
  });

  const stats = [
    { title: 'This Week', value: upcomingThisWeek.length, subtitle: 'upcoming events', icon: Clock, color: 'bg-blue-500' },
    { title: 'This Month', value: upcomingThisMonth.length, subtitle: 'upcoming events', icon: Calendar, color: 'bg-green-500' },
    { title: 'Pending', value: pendingContracts.length, subtitle: 'contracts awaiting', icon: FileText, color: 'bg-yellow-500' },
    { title: 'Monthly Revenue', value: `$${monthlyRevenue.toLocaleString()}`, subtitle: 'this month', icon: TrendingUp, color: 'bg-purple-500' },
  ];

  const quickStats = [
    { label: 'Total Contracts', value: contracts.length },
    { label: 'Confirmed', value: confirmedContracts.length },
    { label: 'Total Revenue', value: `$${totalRevenue.toLocaleString()}` },
    { label: 'Total Guests Served', value: totalGuests.toLocaleString() },
  ];

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen flex justify-center items-center">
        <Loader2 className="animate-spin text-[#1e3a5f]" size={48} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="dashboard-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Dashboard</h1>
        <p className="text-gray-600">Welcome back! Here's your business overview.</p>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <Card key={stat.title} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300" data-testid={`stat-card-${stat.title.replace(/\s+/g, '-').toLowerCase()}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-[#1e3a5f]">{stat.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{stat.subtitle}</p>
                </div>
                <div className={`${stat.color} p-4 rounded-lg`}>
                  <stat.icon className="text-white" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Stats Bar */}
      <Card className="border-none shadow-lg mb-8 bg-gradient-to-r from-[#1e3a5f] to-[#2d5016]">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {quickStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-white/80 text-sm mb-1">{stat.label}</p>
                <p className="text-white text-2xl font-bold">{stat.value}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Business-Line Segmentation */}
      <Card className="border-none shadow-lg mb-8" data-testid="business-line-segmentation">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#1e3a5f]">By Business Line</CardTitle>
          <p className="text-xs text-gray-500">Derived from event type — click to filter contracts</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {businessLineStats.map((bl) => (
              <button
                key={bl.line}
                onClick={() => navigate(`/contracts?line=${encodeURIComponent(bl.line)}`)}
                data-testid={`business-line-card-${bl.line.replace(/\s+/g, '-').toLowerCase()}`}
                className={`text-left p-4 rounded-lg border-2 hover:shadow-md transition ${businessLineColor(bl.line)}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm">{bl.line}</span>
                  <span className="text-xs font-mono opacity-70">{bl.count} total</span>
                </div>
                <p className="text-2xl font-bold">${bl.revenue.toLocaleString()}</p>
                <div className="text-xs mt-1 opacity-80">
                  {bl.upcoming} upcoming · {bl.guests.toLocaleString()} guests
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="border-none shadow-lg mb-8">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#1e3a5f]">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Button 
              onClick={() => navigate('/contracts')} 
              className="h-20 flex flex-col items-center justify-center gap-2 bg-[#1e3a5f] hover:bg-[#2d4a6f]"
              data-testid="quick-new-contract"
            >
              <Plus className="h-6 w-6" />
              <span className="text-xs">New Contract</span>
            </Button>
            <Button 
              onClick={() => navigate('/clients')} 
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-2 hover:bg-gray-50"
              data-testid="quick-new-client"
            >
              <UserPlus className="h-6 w-6 text-[#1e3a5f]" />
              <span className="text-xs text-[#1e3a5f]">Add Client</span>
            </Button>
            <Button 
              onClick={() => navigate('/menus')} 
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-2 hover:bg-gray-50"
              data-testid="quick-menus"
            >
              <Utensils className="h-6 w-6 text-[#1e3a5f]" />
              <span className="text-xs text-[#1e3a5f]">Menus</span>
            </Button>
            <Button 
              onClick={() => navigate('/inventory')} 
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-2 hover:bg-gray-50"
              data-testid="quick-inventory"
            >
              <Package className="h-6 w-6 text-[#1e3a5f]" />
              <span className="text-xs text-[#1e3a5f]">Inventory</span>
            </Button>
            <Button 
              onClick={() => navigate('/calendar')} 
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-2 hover:bg-gray-50"
              data-testid="quick-calendar"
            >
              <CalendarCheck className="h-6 w-6 text-[#1e3a5f]" />
              <span className="text-xs text-[#1e3a5f]">Calendar</span>
            </Button>
            <Button 
              onClick={() => navigate('/pos')} 
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-2 hover:bg-gray-50"
              data-testid="quick-pos"
            >
              <CreditCard className="h-6 w-6 text-[#1e3a5f]" />
              <span className="text-xs text-[#1e3a5f]">POS</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Today's Events Highlight */}
      {todaysEvents.length > 0 && (
        <Card className="border-none shadow-lg mb-8 border-l-4 border-l-[#c9a227]">
          <CardHeader className="bg-gradient-to-r from-[#c9a227] to-[#dbb846] text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Today's Events ({todaysEvents.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {todaysEvents.map((contract) => (
                <div
                  key={contract.id}
                  className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border-2 border-[#c9a227] cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate('/contracts')}
                  data-testid={`todays-event-${contract.id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-[#1e3a5f]">{contract.event_name}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      contract.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {contract.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{contract.client_name}</p>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="font-semibold text-[#c9a227]">{contract.event_time}</span>
                    <span className="text-gray-500">{contract.guest_count} guests</span>
                    <span className="font-bold text-[#1e3a5f]">${contract.total_amount?.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 truncate">{contract.location}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming Events This Week */}
        <Card className="border-none shadow-lg lg:col-span-2">
          <CardHeader className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5016] text-white rounded-t-lg">
            <CardTitle className="flex items-center space-x-2">
              <Calendar size={20} />
              <span>Upcoming This Week ({upcomingThisWeek.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {upcomingThisWeek.length > 0 ? (
              <div className="space-y-4">
                {upcomingThisWeek.slice(0, 5).map((contract) => (
                  <div
                    key={contract.id}
                    className="p-4 bg-white rounded-lg border-l-4 border-[#c9a227] hover:shadow-md transition-shadow duration-200 cursor-pointer"
                    onClick={() => navigate('/contracts')}
                    data-testid={`upcoming-event-${contract.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-[#1e3a5f] mb-1">{contract.event_name}</h3>
                        <p className="text-sm text-gray-600">{contract.client_name}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        contract.status === 'confirmed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {contract.status}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <span>{formatLocalDate(contract.event_date, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                      <span>•</span>
                      <span>{contract.event_time}</span>
                      <span>•</span>
                      <span>{contract.guest_count} guests</span>
                      <span>•</span>
                      <span className="font-semibold text-[#1e3a5f]">${contract.total_amount?.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="mx-auto text-gray-300 mb-4" size={48} />
                <p className="text-gray-500">No events scheduled this week</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alerts & Actions */}
        <div className="space-y-6">
          {/* Pending Contracts */}
          <Card className="border-none shadow-lg">
            <CardHeader className="bg-yellow-500 text-white rounded-t-lg py-4">
              <CardTitle className="flex items-center space-x-2 text-lg">
                <FileText size={18} />
                <span>Pending Contracts ({pendingContracts.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {pendingContracts.length > 0 ? (
                <div className="space-y-3">
                  {pendingContracts.slice(0, 3).map((contract) => (
                    <div
                      key={contract.id}
                      className="p-3 bg-yellow-50 rounded-lg cursor-pointer hover:bg-yellow-100"
                      onClick={() => navigate('/contracts')}
                    >
                      <p className="font-semibold text-sm text-[#1e3a5f]">{contract.event_name}</p>
                      <p className="text-xs text-gray-600">{contract.client_name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <CheckCircle className="mx-auto text-green-500 mb-2" size={32} />
                  <p className="text-sm text-gray-500">All contracts confirmed!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Low Stock Alerts */}
          <Card className="border-none shadow-lg">
            <CardHeader className="bg-red-500 text-white rounded-t-lg py-4">
              <CardTitle className="flex items-center space-x-2 text-lg">
                <AlertTriangle size={18} />
                <span>Low Stock Alerts</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {lowStockAlerts.length > 0 ? (
                <div className="space-y-3">
                  {lowStockAlerts.slice(0, 4).map((alert) => (
                    <div
                      key={alert.id || alert.name}
                      className="p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100"
                      onClick={() => navigate('/inventory')}
                    >
                      <p className="font-semibold text-sm text-[#1e3a5f]">{alert.name}</p>
                      <p className="text-xs text-red-600">Only {alert.available} left (threshold: {alert.threshold})</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <CheckCircle className="mx-auto text-green-500 mb-2" size={32} />
                  <p className="text-sm text-gray-500">All stock levels OK</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invoice Summary */}
          {invoiceStats && (
            <Card className="border-none shadow-lg">
              <CardHeader className="bg-gradient-to-r from-[#8b7355] to-[#c4a574] text-white rounded-t-lg py-4">
                <CardTitle className="flex items-center space-x-2 text-lg">
                  <DollarSign size={18} />
                  <span>Payment Status</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Outstanding</span>
                    <span className="font-bold text-red-600">${invoiceStats.outstanding?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Collected</span>
                    <span className="font-bold text-green-600">${invoiceStats.total_collected?.toLocaleString()}</span>
                  </div>
                  <div className="pt-2 border-t">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Unpaid: {invoiceStats.counts?.unpaid}</span>
                      <span>Deposit: {invoiceStats.counts?.deposit_paid}</span>
                      <span>Paid: {invoiceStats.counts?.paid}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* New Quote Requests Widget */}
          <Card className="border-none shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-t-lg py-4">
              <CardTitle className="flex items-center space-x-2 text-lg">
                <FileQuestion size={18} />
                <span>New Quote Requests ({quoteRequests.filter(q => q.status === 'new').length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {quoteRequests.filter(q => q.status === 'new').length > 0 ? (
                <div className="space-y-3">
                  {quoteRequests.filter(q => q.status === 'new').slice(0, 3).map((quote) => (
                    <div
                      key={quote.id}
                      className="p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
                      onClick={() => navigate('/quote-management')}
                      data-testid={`quote-request-${quote.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-sm text-[#1e3a5f]">{quote.name}</p>
                          <p className="text-xs text-gray-600">{quote.guest_count} guests • {new Date(quote.event_date).toLocaleDateString()}</p>
                        </div>
                        <span className="text-xs font-bold text-blue-600">${quote.estimate_details?.total?.toLocaleString() || 0}</span>
                      </div>
                    </div>
                  ))}
                  {quoteRequests.filter(q => q.status === 'new').length > 3 && (
                    <button 
                      onClick={() => navigate('/quote-management')}
                      className="w-full text-center text-sm text-blue-600 hover:text-blue-800 flex items-center justify-center gap-1 pt-2"
                    >
                      View all {quoteRequests.filter(q => q.status === 'new').length} requests
                      <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <FileQuestion className="mx-auto text-gray-300 mb-2" size={32} />
                  <p className="text-sm text-gray-500">No new requests</p>
                  <button 
                    onClick={() => navigate('/quote-management')}
                    className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                  >
                    View all quotes →
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* This Month Overview */}
      <Card className="border-none shadow-lg mt-6">
        <CardHeader className="bg-gradient-to-r from-[#8b7355] to-[#c4a574] text-white rounded-t-lg">
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp size={20} />
            <span>This Month's Events ({upcomingThisMonth.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {upcomingThisMonth.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingThisMonth.map((contract) => (
                <div
                  key={contract.id}
                  className="p-4 bg-white rounded-lg border hover:shadow-md transition-shadow duration-200 cursor-pointer"
                  onClick={() => navigate('/contracts')}
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      contract.event_type === 'onsite' ? 'bg-purple-100 text-purple-800' :
                      contract.event_type === 'offsite' ? 'bg-red-100 text-red-800' :
                      contract.event_type === 'tournament' ? 'bg-green-100 text-green-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {contract.event_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                    <span className="text-sm font-bold text-[#1e3a5f]">${contract.total_amount?.toLocaleString()}</span>
                  </div>
                  <h3 className="font-semibold text-[#1e3a5f]">{contract.event_name}</h3>
                  <p className="text-sm text-gray-600">{contract.client_name}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {formatLocalDate(contract.event_date, { weekday: 'long', month: 'short', day: 'numeric' })} • {contract.guest_count} guests
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No events scheduled this month</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
